import { TrendingUp, AlertTriangle, Quote as QuoteIcon, Eye } from 'lucide-react';
import ProjectQuestionInsights from '../ProjectQuestionInsights';
import VoiceQuotes from '../VoiceQuotes';
import RespondentMediaGallery from '../RespondentMediaGallery';
import AnalysisSection from '@/components/analysis/AnalysisSection';
import NotableVisualsGrid from '@/components/analysis/NotableVisualsGrid';
import Markdown from '@/components/markdown/Markdown';
import { resolveTranslation } from '@/utils/resolveTranslation';
import {
  translateNoKeyThemesData,
  translateNoDivergentViewsData,
  translateDivergentViewsAndOutliers,
  translateVoiceOfTheCustomer,
  translateKeyFindings,
  translateVisualEvidence,
} from '@/pages/projects/single-project/Content/project_results/resultsPageTranslations';
import type { InsightsSubset } from '@/types';

interface ConversationMedia {
  conversationId: string;
  respondentIndex: number;
  media: Array<{
    fileId: string;
    url: string;
    type: string;
    transcriptionText?: string;
  }>;
}

interface EvidenceZoneProps {
  insights?: InsightsSubset;
  isLoading: boolean;
  analysisLanguage: string;
  questionMedia: ConversationMedia[];
  isMediaLoading: boolean;
  voiceQuotes?: Array<{ conversation_id: string; message_id?: string }>;
}

function EvidenceZone({
  insights,
  isLoading,
  analysisLanguage,
  questionMedia,
  isMediaLoading,
  voiceQuotes,
}: EvidenceZoneProps) {
  return (
    <div className="flex flex-col gap-5">
      {/* Key Findings */}
      <ProjectQuestionInsights
        data={insights?.patterns_and_trends ?? (!insights?.themes ? undefined : insights.themes)}
        title={resolveTranslation(translateKeyFindings, analysisLanguage)}
        isLoading={isLoading}
        noDataText={resolveTranslation(translateNoKeyThemesData, analysisLanguage)}
        icon={<TrendingUp className="h-5 w-5" />}
        voiceQuotes={voiceQuotes}
      />

      {/* Divergent Views — no fallback to opportunities (DataZone handles those) */}
      <ProjectQuestionInsights
        data={insights?.divergent_views_and_outliers}
        title={resolveTranslation(translateDivergentViewsAndOutliers, analysisLanguage)}
        isLoading={isLoading}
        noDataText={resolveTranslation(translateNoDivergentViewsData, analysisLanguage)}
        icon={<AlertTriangle className="h-5 w-5" />}
        voiceQuotes={voiceQuotes}
      />

      {/* Voice of the Customer */}
      {insights?.voice_quotes && insights.voice_quotes.length > 0 && (
        <AnalysisSection
          title={resolveTranslation(translateVoiceOfTheCustomer, analysisLanguage)}
          icon={<QuoteIcon className="h-5 w-5" />}
          fullWidth
        >
          <VoiceQuotes quotes={insights.voice_quotes} isLoading={isLoading} />
        </AnalysisSection>
      )}

      {/* Visual Evidence — merged visual observations + question visuals + media gallery */}
      {((insights?.visual_observations && insights.visual_observations.length > 0) ||
        (insights?.question_visuals && insights.question_visuals.length > 0) ||
        (questionMedia && questionMedia.length > 0)) && (
        <AnalysisSection
          title={resolveTranslation(translateVisualEvidence, analysisLanguage)}
          icon={<Eye className="h-5 w-5" />}
          fullWidth
          copyText={insights?.visual_observations || undefined}
        >
          <div className="flex flex-col gap-4">
            {/* AI visual observations text */}
            {insights?.visual_observations && <Markdown>{insights.visual_observations}</Markdown>}

            {/* AI-selected notable visuals */}
            {insights?.question_visuals && insights.question_visuals.length > 0 && (
              <div className="mt-2">
                <NotableVisualsGrid
                  visuals={insights.question_visuals.map((v) => ({
                    url: v.url,
                    type: v.type,
                    caption: v.caption,
                    respondentIndex: v.respondent_index,
                  }))}
                />
              </div>
            )}

            {/* Respondent media gallery */}
            {questionMedia && questionMedia.length > 0 && (
              <RespondentMediaGallery
                data={questionMedia}
                isLoading={isMediaLoading}
                analysisLanguage={analysisLanguage}
              />
            )}
          </div>
        </AnalysisSection>
      )}
    </div>
  );
}

export default EvidenceZone;
